/**
 * Luật thuần của màn tải chứng từ lên nhận dạng: kiểm tệp trước khi gửi theo đúng giới hạn của
 * `POST /documents/upload` (`UPLOAD_MIMES`, `MAX_DOCUMENT_FILES`, `OCR_UPLOAD_MAX_FILE_MB`,
 * `OCR_UPLOAD_MAX_TOTAL_MB` mặc định), và gom dòng của chứng từ theo bảng. Trả **mã** để view tự
 * dịch. Server vẫn kiểm lại; đây chỉ để báo sớm, ngay dưới tệp sai.
 */

export const TOI_DA_SO_TEP = 20;
export const TOI_DA_MOI_TEP = 25 * 1024 * 1024;
export const TOI_DA_TONG = 60 * 1024 * 1024;

export const KIEU_NHAN = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/tiff',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'text/plain',
  'application/csv',
]);

/** Thuộc tính `accept` của ô chọn tệp: đuôi tệp cho hộp chọn của hệ điều hành. */
export const ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.tif,.tiff,.docx,.xlsx,.xls,.csv,.txt';

export type LoiTep = 'sai-dinh-dang' | 'qua-lon';

export interface KetQuaKiemTep {
  theoTep: Array<{ chiSo: number; loi: LoiTep }>;
  quaNhieuTep: boolean;
  tongQuaLon: boolean;
  hopLe: boolean;
}

export function kiemTepNhanDang(tep: ReadonlyArray<{ type: string; size: number }>): KetQuaKiemTep {
  const theoTep: KetQuaKiemTep['theoTep'] = [];
  tep.forEach((f, chiSo) => {
    if (!KIEU_NHAN.has(f.type)) theoTep.push({ chiSo, loi: 'sai-dinh-dang' });
    else if (f.size > TOI_DA_MOI_TEP) theoTep.push({ chiSo, loi: 'qua-lon' });
  });
  const quaNhieuTep = tep.length > TOI_DA_SO_TEP;
  const tongQuaLon = tep.reduce((n, f) => n + f.size, 0) > TOI_DA_TONG;
  return { theoTep, quaNhieuTep, tongQuaLon, hopLe: tep.length > 0 && theoTep.length === 0 && !quaNhieuTep && !tongQuaLon };
}

/** Chỉ số tệp trong `#/files/{i}` của lỗi server, để hiện dưới đúng tệp. */
export function chiSoTepLoi(pointer: string): number | null {
  const m = /^\/files\/(\d+)$/.exec(pointer.replace(/^#/, ''));
  return m ? Number(m[1]) : null;
}

/**
 * Gom dòng của chứng từ theo bảng. Dòng `table_key = null` (dữ liệu một bảng của bản cũ) thuộc bảng
 * đầu tiên, như `result()` của backend. Giá trị một ô: cột chuẩn (`name`, `unit`, `quantity`,
 * `unit_price`, `amount`) rồi `extra_data` theo `column_key` (`publicRow` của backend).
 */
export function gomDongTheoBang<
  T extends { table_key: string | null; stt: number },
>(bangKhoa: readonly string[], dong: readonly T[]): Map<string, T[]> {
  const ra = new Map<string, T[]>(bangKhoa.map((k) => [k, []]));
  const dau = bangKhoa[0];
  for (const d of dong) {
    const k = d.table_key ?? dau;
    if (k === undefined) continue;
    if (!ra.has(k)) ra.set(k, []);
    ra.get(k)!.push(d);
  }
  for (const ds of ra.values()) ds.sort((a, b) => a.stt - b.stt);
  return ra;
}

export function giaTriO(
  d: { name: string | null; unit: string | null; quantity: unknown; unit_price: unknown; amount: unknown; extra_data: Record<string, unknown> | null },
  columnKey: string,
): unknown {
  const chuan: Record<string, unknown> = { name: d.name, unit: d.unit, quantity: d.quantity, unit_price: d.unit_price, amount: d.amount };
  if (d.extra_data && columnKey in d.extra_data) return d.extra_data[columnKey];
  return chuan[columnKey] ?? null;
}
